import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import {
  useQuery,
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

const queryKeys = {
  all: () => ['skinning-sites'] as const,
  search: (searchBody: string) =>
    [...queryKeys.all(), 'search', searchBody] as const,
};

export interface Content {
  companyName: string;
}

interface ColorVariants {
  x50?: string;
  x200?: string;
  default?: string;
  x700?: string;
}

interface CorePalette {
  background?: string;
  primary?: ColorVariants;
  secondary?: ColorVariants;
  border?: string;
  grey?: ColorVariants;
  black?: string;
}

interface CoreShape {
  borderRadius?: string;
  buttonRadius?: string;
}

interface CoreTypography {
  fontFamily?: string;
}

interface CoreTheme {
  palette?: CorePalette;
  shape?: CoreShape;
  typography?: CoreTypography;
}

interface Theme {
  core?: CoreTheme;
  fontSrc?: string;
}
export interface ThemeOverride {
  theme?: Theme;
  content: Content;
}

export interface Filters {
  domain?: string;
}

export interface SearchResponse {
  aggregatedData: {
    count: number;
  };
  sites: { domain: string; theme: string }[];
}

type Pagination = { size: number; page: number };

export const useSearch = (filters: Filters, pagination: Pagination) => {
  return useQuery({
    queryKey: queryKeys.search(JSON.stringify({ ...filters, ...pagination })),
    queryFn: async () => {
      const data = await fetchAPI<SearchResponse>(
        `v1/skinning-site-themes:search`,
        {
          method: 'POST',
          body: {
            pagination,
            filters: filters,
          },
        }
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      domain,
      theme,
    }: {
      domain: string;
      theme: string;
    }) => {
      await fetchAPI(`v1/skinning-site-themes`, {
        method: 'POST',
        body: {
          domain,
          theme,
        },
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.all(),
      });
    },
  });
};
